import { Children, cloneElement, type ReactNode, useState } from 'react'
import { mergeProps } from 'react-aria'
import {
  Dialog,
  DialogTrigger,
  type DialogTriggerProps,
  Popover as AriaPopover,
  type PopoverProps as AriaPopoverProps,
  Pressable
} from 'react-aria-components'

import { cx } from '@utils/cx'

/**
 * A popover component for displaying interactive or complex content in an overlay.
 *
 * This component provides a flexible way to show contextual content that appears on top of the main interface.
 * Unlike tooltips, popovers are designed for interactive content and can contain buttons, forms, or other complex UI elements.
 *
 * @example
 * Basic usage with automatic open/close behavior:
 * ```tsx
 * <Popover>
 *   <Button>Open Popover</Button>
 *   <PopoverContent aria-label="User actions">
 *     <p>This is popover content</p>
 *     <Button>Action</Button>
 *   </PopoverContent>
 * </Popover>
 * ```
 *
 * @example
 * Controlled usage with manual state management:
 * ```tsx
 * const [isOpen, setIsOpen] = useState(false)
 *
 * <Popover isOpen={isOpen} onOpenChange={setIsOpen}>
 *   <Button>Toggle Popover</Button>
 *   <PopoverContent aria-label="Settings menu">
 *     <div>Complex interactive content here</div>
 *   </PopoverContent>
 * </Popover>
 * ```
 *
 */
export function Popover({
  children,
  onOpenChange: externalOnOpenChange,
  ...props
}: DialogTriggerProps) {
  const [internalOpen, setInternalOpen] = useState(false)

  // Determine if we're using manual control
  const isManual = props.isOpen !== undefined
  const isOpen = props.isOpen ?? internalOpen

  const handleOpenChange = (open: boolean) => {
    setInternalOpen(open)
    externalOnOpenChange?.(open)
  }

  // We must separate children to add props to the trigger.
  const [triggerElement, contentElement] = Children.toArray(children)

  if (!triggerElement || !contentElement) {
    throw new Error('Popover requires exactly two children: a trigger element (first child) and a content element (second child)')
  }

  // Merge all conditional props safely.
  const triggerWithProps = cloneElement(
    triggerElement as React.ReactElement,
    mergeProps(
      (triggerElement as React.ReactElement).props ?? {},
      !isManual ? { onPress: () => handleOpenChange(!isOpen) } : {}
    )
  )

  return (
    <DialogTrigger isOpen={isOpen} onOpenChange={handleOpenChange}>
      {triggerWithProps}
      {contentElement}
    </DialogTrigger>
  )
}

/**
 * Props interface for PopoverContent component.
 * Extends AriaPopoverProps but overrides the children prop to be more flexible.
 */
interface MyPopoverProps extends Omit<AriaPopoverProps, 'children'> {
    /** The content to display inside the popover */
    children: ReactNode
    /** Class name for custom styling */
    dialogClassName?: string
}

/**
 * The content container for a popover.
 *
 * This component wraps the actual content of the popover and provides default styling
 * including animations, shadows, and padding. It should be used as the second child of the Popover component.
 *
 */
export function PopoverContent({ children, 'aria-label': ariaLabel, dialogClassName, ...props }: MyPopoverProps) {
  return (
    <AriaPopover
      {...props}
      className="entering:animate-in entering:fade-in exiting:animate-out exiting:fade-out"
    >
      <Dialog className={cx('rounded-lg bg-white px-3 py-2 shadow-md', dialogClassName)} aria-label={ariaLabel} >
        {children}
      </Dialog>
    </AriaPopover>
  )
}

/**
 * A wrapper component for custom trigger elements that aren't React Aria components.
 *
 * While the Popover works automatically with React Aria pressable components (Button, Link, etc.),
 * custom trigger elements like third-party components or plain DOM elements need to be wrapped
 * with this component to work properly with the popover system.
 *
 * @example
 * Using with a custom div element:
 * ```tsx
 * <Popover>
 *   <PopoverTrigger>
 *     <div className="custom-trigger">
 *       Click me to open popover
 *     </div>
 *   </PopoverTrigger>
 *   <PopoverContent aria-label="Custom content">
 *     Content here
 *   </PopoverContent>
 * </Popover>
 * ```
 *
 * @see {@link https://react-spectrum.adobe.com/react-aria/Popover.html#custom-trigger React Aria Popover Documentation}
 */
export const PopoverTrigger = Pressable